import {
  isEditableEventTarget,
  type DeferredDismissState,
} from '@littlebird/action-cards';
import { useEffect, useId, useRef } from 'react';
import { toast } from 'sonner';

import { DismissToast, type DismissToastMode } from './components/DismissToast';

// Only list toasts carry this prefix, so Cmd+Z never lands on a chat toast, which has no Undo
const TOAST_PREFIX = 'action-card-dismiss:';
const CHAT_TOAST_PREFIX = 'action-card-discard:';

export const useDismissToast = (
  controller: DeferredDismissState,
  mode: DismissToastMode = 'list'
) => {
  const ownerId = useId();
  const toastIds = useRef(new Map<string, string>());
  const sequence = useRef(0);

  useEffect(() => {
    // A chat toast with no question would offer nothing: the folded card has Restore
    const shownStrips = controller.strips.filter(
      (strip) => mode === 'list' || strip.question !== null
    );
    const activeIds = new Set(shownStrips.map((strip) => strip.id));

    for (const [stripId, toastId] of toastIds.current) {
      if (activeIds.has(stripId)) continue;

      toast.dismiss(toastId);
      toastIds.current.delete(stripId);
    }

    for (const strip of shownStrips) {
      let toastId = toastIds.current.get(strip.id);
      if (!toastId) {
        const prefix = mode === 'list' ? TOAST_PREFIX : CHAT_TOAST_PREFIX;
        toastId = `${prefix}${ownerId}:${sequence.current++}`;
        toastIds.current.set(strip.id, toastId);
      }

      // The controller, not Sonner, owns the undo deadline, so the toast never times out itself
      toast.custom(
        () => (
          <DismissToast strip={strip} controller={controller} mode={mode} />
        ),
        { id: toastId, duration: Infinity, dismissible: false }
      );
    }

    if (mode === 'chat' || shownStrips.length === 0) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (
        event.defaultPrevented ||
        isEditableEventTarget(event.target) ||
        !(event.metaKey || event.ctrlKey) ||
        event.altKey ||
        event.shiftKey ||
        event.key.toLowerCase() !== 'z'
      )
        return;

      // Only the newest dismissal owns Undo, even across different surfaces
      const latestToast = toast
        .getToasts()
        .filter(
          ({ id }) => typeof id === 'string' && id.startsWith(TOAST_PREFIX)
        )
        .at(-1);

      const stripId = [...toastIds.current].find(
        ([, toastId]) => toastId === latestToast?.id
      )?.[0];

      if (!stripId) return;

      event.preventDefault();
      controller.undo(stripId);
    };

    window.addEventListener('keydown', onKeyDown);

    return () => window.removeEventListener('keydown', onKeyDown);
  }, [controller, mode, ownerId]);

  useEffect(() => {
    const ids = toastIds.current;

    return () => {
      for (const toastId of ids.values()) toast.dismiss(toastId);

      ids.clear();
    };
  }, []);
};
